// authThunks.js - FIXED ERROR HANDLING FOR DEVICE VALIDATION ERRORS
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';
import deviceService from '../../services/deviceService';

// ============================================================================
// LOGIN THUNK - FIXED ERROR HANDLING
// ============================================================================

export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      // Step 1: Try to get device credentials (but don't fail if unavailable)
      let deviceCredentials = null;
      
      try {
        deviceCredentials = await deviceService.getDeviceCredentials();
        console.log('✓ Device credentials obtained');
      } catch (deviceError) {
        console.warn('Device agent not available:', deviceError);
        // DON'T reject here - let backend decide if this is OK
        // Admins can login without device agent, non-admins cannot
      }

      // Step 2: Prepare login data
      const loginData = {
        username,
        password,
      };

      // Include device credentials if available
      if (deviceCredentials) {
        loginData.device_key = deviceCredentials.device_key;
        loginData.mac_address = deviceCredentials.mac_address;
        loginData.computer_name = deviceCredentials.computer_name;
        console.log('✓ Device credentials included in login request');
      } else {
        console.log('⚠ No device credentials - backend will check if user is admin');
      }

      // Step 3: Attempt login - backend will enforce device requirements
      const res = await axiosInstance.post('/api/token/', loginData);
      const { access, refresh } = res.data;

      // Step 4: Store tokens
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);

      // Step 5: Get user profile
      const { data: user } = await axiosInstance.get('/api/whoami/', {
        headers: { Authorization: `Bearer ${access}` },
      });

      localStorage.setItem('user', JSON.stringify(user));

      return { user, access, refresh };
      
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response);
      
      // 🔧 FIXED: Extract error message from DRF ValidationError (400 status)
      if (err.response?.status === 400) {
        // DRF ValidationError returns errors in multiple possible formats:
        // Format 1: { "detail": ["error message"] }
        // Format 2: { "non_field_errors": ["error message"] }
        // Format 3: { "field_name": ["error message"] }
        
        const data = err.response.data;
        
        // Try to extract the error message
        let errorMessage = 'Login failed. Please check your credentials.';
        
        if (data.detail) {
          // Handle detail field (can be string or array)
          errorMessage = Array.isArray(data.detail) 
            ? data.detail[0] 
            : data.detail;
        } else if (data.non_field_errors) {
          // Handle non_field_errors array
          errorMessage = Array.isArray(data.non_field_errors)
            ? data.non_field_errors[0]
            : data.non_field_errors;
        } else if (typeof data === 'string') {
          // Handle plain string response
          errorMessage = data;
        } else {
          // Handle field-specific errors (take first error found)
          const firstError = Object.values(data)[0];
          if (firstError) {
            errorMessage = Array.isArray(firstError) 
              ? firstError[0] 
              : firstError;
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      // Handle 403 Forbidden
      if (err.response?.status === 403) {
        return rejectWithValue(
          err.response.data.message || 
          err.response.data.detail || 
          'Access denied. This device may not be authorized.'
        );
      }
      
      // Handle 401 Unauthorized
      if (err.response?.status === 401) {
        return rejectWithValue('Invalid username or password');
      }
      
      // Handle network errors
      if (err.message) {
        return rejectWithValue(err.message);
      }
      
      // Fallback
      return rejectWithValue(
        'Could not log in. Please try again.'
      );
    }
  }
);

// ============================================================================
// EXISTING THUNKS (Keep these as they are)
// ============================================================================

// LOGOUT THUNK
export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (navigate, { rejectWithValue }) => {
    try {
      // Clear localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Navigate to login if navigate function is provided
      if (navigate) {
        navigate('/login');
      }
      
      return {};
    } catch (err) {
      return rejectWithValue('Logout failed');
    }
  }
);

// BOOTSTRAP THUNK (run once on app start)
export const bootstrapAuth = createAsyncThunk(
  'auth/bootstrap',
  async (_, { rejectWithValue }) => {
    const access = localStorage.getItem('accessToken');
    if (!access) return rejectWithValue();

    try {
      const { data: user } = await axiosInstance.get('/api/whoami/', {
        headers: { Authorization: `Bearer ${access}` },
      });
      return { user };
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return rejectWithValue();
    }
  }
);

// ============================================================================
// FORGOT PASSWORD THUNKS
// ============================================================================

/**
 * Request OTP for password reset
 */
export const requestOTPThunk = createAsyncThunk(
  'auth/requestOTP',
  async ({ identifier }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/password-reset/request-otp/', {
        identifier,
      });
      
      return response.data;
    } catch (err) {
      console.error('Request OTP error:', err);
      
      return rejectWithValue(
        err.response?.data?.error ||
        err.response?.data?.identifier?.[0] ||
        err.response?.data?.detail ||
        'Failed to send OTP. Please try again.'
      );
    }
  }
);

/**
 * Verify OTP
 */
export const verifyOTPThunk = createAsyncThunk(
  'auth/verifyOTP',
  async ({ identifier, otp }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/password-reset/verify-otp/', {
        identifier,
        otp,
      });
      
      return response.data;
    } catch (err) {
      console.error('Verify OTP error:', err);
      
      return rejectWithValue(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to verify OTP. Please try again.'
      );
    }
  }
);

/**
 * Reset password after OTP verification
 */
export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async ({ identifier, otp, new_password, confirm_password }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/password-reset/reset-password/', {
        identifier,
        otp,
        new_password,
        confirm_password,
      });
      
      return response.data;
    } catch (err) {
      console.error('Reset password error:', err);
      
      // Handle validation errors
      if (err.response?.data?.new_password) {
        return rejectWithValue(
          Array.isArray(err.response.data.new_password)
            ? err.response.data.new_password.join(' ')
            : err.response.data.new_password
        );
      }
      
      if (err.response?.data?.confirm_password) {
        return rejectWithValue(err.response.data.confirm_password);
      }
      
      return rejectWithValue(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to reset password. Please try again.'
      );
    }
  }
);

/**
 * Resend OTP
 */
export const resendOTPThunk = createAsyncThunk(
  'auth/resendOTP',
  async ({ identifier }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/password-reset/resend-otp/', {
        identifier,
      });
      
      return response.data;
    } catch (err) {
      console.error('Resend OTP error:', err);
      
      return rejectWithValue(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to resend OTP. Please try again.'
      );
    }
  }
);